// Policy for the "install the browser extension" prompt.
//
// The whole point of the presence marker (see packages/extension/src/content) is
// that we can suppress this for people who already have the extension, so the
// prompt never has to be shy with the people who don't. What's left to decide is
// how often to ask the rest, and when to stop asking.
//
// Kept free of React so the rules can be tested directly.

/** Chrome Web Store listing for Eizel Saver. */
export const EXTENSION_STORE_URL =
  'https://chromewebstore.google.com/detail/fhchodjfldilhajmjelaolbddchkfpba'

/** Attribute the extension's content script stamps on <html>. */
const MARKER_ATTRIBUTE = 'data-eizel-saver'

/** First visit that may see the prompt — never on visit 1. A brand new user
 *  should see what the app does before being sold an add-on for it. */
export const FIRST_VISIT = 2
/** Visits between showings, so: 2, 7, 12. */
export const VISIT_INTERVAL = 5
/** Hard stop. Someone who has seen it three times has answered, whether or not
 *  they ever clicked anything. */
export const MAX_IMPRESSIONS = 3

const VISITS_KEY = 'eizel.ext.visits'
const IMPRESSIONS_KEY = 'eizel.ext.impressions'
const DISMISSED_KEY = 'eizel.ext.dismissed'
/** Lives in sessionStorage, not localStorage: it is what makes one browsing
 *  session count as one visit rather than one route change.
 *
 *  sessionStorage is per-TAB, so N tabs opened at once (or a session restore)
 *  count as N visits and reach an eligible visit sooner than a real return
 *  visitor would. The MAX_IMPRESSIONS cap still bounds the total damage to
 *  three showings. Making a visit time-based instead would fix the inflation;
 *  it is deliberately not done here because it also changes what "a visit"
 *  means. */
const SESSION_KEY = 'eizel.ext.session'

interface UserAgentBrand {
  brand: string
}
interface UserAgentData {
  brands?: UserAgentBrand[]
  mobile?: boolean
}

// Storage throws rather than no-ops in a few real configurations (Safari private
// browsing, cookies and site data blocked entirely, sandboxed frames). Every
// access goes through these, and the failure direction is deliberate: if we
// cannot count visits we cannot rate-limit the prompt, and a prompt that cannot
// be rate-limited should not be shown.
//
// Note that `window.localStorage` must be read INSIDE the try. When site data is
// blocked it is the property getter itself that throws, not getItem — reading it
// at the call site and passing it in would throw past every guard here and take
// the page down with it.
function storageOrNull(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

function readNumber(kind: 'local' | 'session', key: string): number | null {
  try {
    const storage = storageOrNull(kind)
    if (!storage) return null
    const raw = storage.getItem(key)
    if (raw === null) return 0
    const n = Number.parseInt(raw, 10)
    return Number.isFinite(n) && n >= 0 ? n : 0
  } catch {
    return null
  }
}

function write(kind: 'local' | 'session', key: string, value: string): boolean {
  try {
    const storage = storageOrNull(kind)
    if (!storage) return false
    storage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

function has(kind: 'local' | 'session', key: string): boolean | null {
  try {
    const storage = storageOrNull(kind)
    if (!storage) return null
    return storage.getItem(key) !== null
  } catch {
    return null
  }
}

/** True when the extension's content script has stamped this page. */
export function isExtensionInstalled(): boolean {
  return document.documentElement.hasAttribute(MARKER_ATTRIBUTE)
}

/** The installed extension's version, or null if it isn't installed. */
export function installedExtensionVersion(): string | null {
  return document.documentElement.getAttribute(MARKER_ATTRIBUTE)
}

/**
 * Chromium on desktop — the only place a Chrome Web Store link leads anywhere
 * useful. Safari and Firefox users would get a dead end, and mobile Chrome
 * cannot install extensions at all.
 */
export function isChromiumDesktop(): boolean {
  const uaData = (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData
  if (uaData?.brands) {
    if (uaData.mobile) return false
    return uaData.brands.some((b) => b.brand === 'Chromium')
  }
  const ua = navigator.userAgent
  if (/Android|iPhone|iPad|iPod/.test(ua)) return false
  // Every Chromium desktop browser carries "Chrome/"; Safari and Firefox do not.
  return /Chrome\//.test(ua)
}

/**
 * Counts this browsing session as one visit, at most once. Route changes inside
 * the SPA must not count — an afternoon of reading is one visit, not fifteen.
 *
 * Returns the running visit count, or null if storage is unavailable.
 */
export function registerVisit(): number | null {
  const visits = readNumber('local', VISITS_KEY)
  if (visits === null) return null

  const alreadyCounted = has('session', SESSION_KEY)
  if (alreadyCounted === null) return null
  if (alreadyCounted) return visits

  const next = visits + 1
  if (!write('local', VISITS_KEY, String(next))) return null
  write('session', SESSION_KEY, '1')
  return next
}

/** Whether the prompt should be shown on this visit. */
export function shouldShowPrompt(visits: number | null): boolean {
  if (visits === null) return false
  if (isExtensionInstalled()) return false
  if (!isChromiumDesktop()) return false

  const dismissed = has('local', DISMISSED_KEY)
  if (dismissed === null || dismissed) return false

  const impressions = readNumber('local', IMPRESSIONS_KEY)
  if (impressions === null || impressions >= MAX_IMPRESSIONS) return false

  if (visits < FIRST_VISIT) return false
  return (visits - FIRST_VISIT) % VISIT_INTERVAL === 0
}

/**
 * Records that the prompt was actually rendered.
 *
 * Read-then-write, and localStorage has no atomic increment. Two tabs that both
 * land on an eligible visit in the same instant can lose one increment, which
 * spends the impression cap slightly slower than intended. Bounded and rare
 * (only visits 2, 7, and 12 are eligible at all), so it is not worth a lock.
 */
export function recordImpression(): void {
  const impressions = readNumber('local', IMPRESSIONS_KEY)
  if (impressions === null) return
  write('local', IMPRESSIONS_KEY, String(impressions + 1))
}

/** Retires the prompt permanently — the user has answered. */
export function dismissForever(): void {
  write('local', DISMISSED_KEY, '1')
}
